// Gridd's factory-default stylesheet (TODO §7.4 / §2.4).
//
// A deck's own `styleSheet` starts empty (`sheets: {}`) and only fills in when the user
// actually saves from the stylesheet editor. The Gridd app, however, is never unstyled:
// its styleSheet slice ships `sheets.default` as a factory sheet and renders every grid
// against it. So an untouched deck used to look styled inside the editor panel (which
// streams a draft of that factory sheet to us) and revert to hardcoded fallbacks the
// moment the panel closed — the same deck, two different looks.
//
// This is that factory sheet, copied verbatim from @gridd/embeddable-dist 1.4.0 (and
// re-checked byte-for-byte against 1.4.1, which did not touch it)
// (`styleSheet` slice initialState → `sheets.default`), so SlideCraft's own chrome and
// text blocks resolve against exactly what the grids are already drawing with. It is a
// READ-side fallback only: it is never written into a deck, never persisted, and never
// pushed to an embed — the app supplies its own copy there. A deck that saves a sheet
// overrides it entirely.
//
// Refresh it by re-extracting from the package when @gridd/embeddable-dist changes its
// factory defaults; nothing here is authored by us.

import type { GriddSheet } from "@/types/document";

export const GRIDD_FACTORY_SHEET: GriddSheet = {
  presets: [
    {
      name: "Header",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "bold",
      fontStyle: "normal",
      fontSize: "16pt",
      fontUnderlined: false,
    },
    {
      name: "SubHeader",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "bold",
      fontStyle: "normal",
      fontSize: "14pt",
      fontUnderlined: false,
    },
    {
      name: "Body",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "normal",
      fontStyle: "normal",
      fontSize: "12pt",
      fontUnderlined: false,
    },
    {
      name: "Highlight",
      fontFamily: "Calisto MT",
      fontColor: "accent2",
      fontColor2: "text2",
      fontWeight: "bold",
      fontStyle: "normal",
      fontSize: "22pt",
      fontUnderlined: false,
    },
    {
      name: "SmallBody",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "normal",
      fontStyle: "normal",
      fontSize: "10pt",
      fontUnderlined: false,
    },
    {
      name: "Caption",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "normal",
      fontStyle: "italic",
      fontSize: "8pt",
      fontUnderlined: false,
    },
    {
      name: "Custom1",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "normal",
      fontStyle: "normal",
      fontSize: "12pt",
      fontUnderlined: false,
    },
    {
      name: "Custom2",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "normal",
      fontStyle: "normal",
      fontSize: "12pt",
      fontUnderlined: false,
    },
    {
      name: "Custom3",
      fontFamily: "Arial",
      fontColor: "text",
      fontColor2: "text2",
      fontWeight: "normal",
      fontStyle: "normal",
      fontSize: "12pt",
      fontUnderlined: false,
    },
  ],
  shapePresets: [
    {
      name: "Row Header",
      label: "H",
      shapeType: "RoundedRectangle",
      radius: 0,
      shapeColor: "accent1",
      outline: "none",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      margin: 5,
      marginPerc: 0,
      marginLeft: 0,
      marginLeftPerc: 0,
      textPreset: "Header",
      inverted: true,
      textAlign: "left",
      verticalAlign: "middle",
      padding: 10,
    },
    {
      name: "Row Sub-Header",
      label: "S",
      shapeType: "RoundedRectangle",
      radius: 0,
      shapeColor: "secondary2",
      outline: "solid",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      margin: 5,
      marginPerc: 0,
      marginLeft: 0,
      marginLeftPerc: 0,
      textPreset: "SubHeader",
      inverted: false,
      textAlign: "left",
      verticalAlign: "middle",
      padding: 9,
    },
    {
      name: "Column Header",
      label: "C",
      shapeType: "RoundedRectangle",
      radius: 0,
      shapeColor: "accent1",
      outline: "none",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      margin: 5,
      marginPerc: 0,
      marginBottom: 0,
      marginBottomPerc: 0,
      textPreset: "Header",
      inverted: true,
    },
    {
      name: "Default Arrow",
      label: "A",
      shapeType: "ArrowThin",
      shapeColor: "secondary1",
      outline: "none",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      textPreset: "Body",
      inverted: false,
    },
    {
      name: "Process Flow",
      label: "F",
      shapeType: "ArrowFull",
      shapeColor: "accent1",
      outline: "none",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      margin: 3,
      marginRightPerc: -5,
      textPreset: "Header",
      inverted: true,
      textAlign: "left",
      verticalAlign: "middle",
      padding: 10,
    },
    {
      name: "Key Takeaway Box",
      label: "KT",
      shapeType: "RoundedRectangle",
      radius: 25,
      shapeColor: "accent2",
      outline: "none",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      margin: 5,
      textPreset: "Body",
      inverted: true,
    },
    {
      name: "Tracker",
      label: "1",
      shapeType: "Circle",
      shapeColor: "background",
      outline: "solid",
      outlineWeight: 1,
      outlineColor: "accent1",
      shadow: 0,
      margin: 0,
      marginPerc: 0,
      textPreset: "Header",
      inverted: false,
    },
  ],
  defaults: {
    preset: "Body",
    border: "level3",
    color: "text",
    spacer: "medium",
  },
  colors: [
    {
      name: "text",
      value: "#333333",
    },
    {
      name: "background",
      value: "#ffffff",
    },
    {
      name: "text2",
      value: "#FFFFFF",
    },
    {
      name: "background2",
      value: "#002C58",
    },
    {
      name: "accent1",
      value: "#001866",
    },
    {
      name: "accent2",
      value: "#4C05FF",
    },
    {
      name: "secondary1",
      value: "#5Da8f9",
    },
    {
      name: "secondary2",
      value: "#e8f0fa",
    },
    {
      name: "shading1",
      value: "#eAeaea",
    },
    {
      name: "shading2",
      value: "#c0c0c0",
    },
    {
      name: "hyperlink",
      value: "#362fec",
    },
    {
      name: "followedhyperlink",
      value: "#9C1FFE",
    },
    {
      name: "sequential1",
      value: "#1d25c4",
    },
    {
      name: "sequential2",
      value: "#6166d6",
    },
    {
      name: "sequential3",
      value: "#8e92e1",
    },
    {
      name: "sequential4",
      value: "#BBBEED",
    },
    {
      name: "sequential5",
      value: "#E8E9F9",
    },
    {
      name: "diverging1",
      value: "#4DC1FF",
    },
    {
      name: "diverging2",
      value: "#4170F3",
    },
    {
      name: "diverging3",
      value: "#7A6DEF",
    },
    {
      name: "diverging4",
      value: "#B36BF1",
    },
    {
      name: "diverging5",
      value: "#DB64FF",
    },
    {
      name: "success",
      value: "#009917",
    },
    {
      name: "warning",
      value: "#FFD000",
    },
    {
      name: "danger",
      value: "#DD0000",
    },
  ],
  fontFamilies: [
    "Arial",
    "Bahnschrift",
    "Baskerville Old Face",
    "Bell MT",
    "Bodoni MT",
    "Book Antiqua",
    "Bookman Old Style",
    "Calibri",
    "Calibri Light",
    "Calisto MT",
    "Cambria",
    "Candara",
    "Centaur",
    "Century",
    "Century Gothic",
    "Consolas",
    "Constantia",
    "Corbel",
    "Franklin Gothic Book",
    "Garamond",
    "Georgia",
    "Gill Sans MT",
    "Lucida Sans",
    "Palatino Linotype",
    "Perpetua",
    "Rockwell",
    "Sans Serif Collection",
    "Segoe UI",
    "Sylfaen",
    "Tahoma",
    "Times New Roman",
    "Trebuchet MS",
    "Verdana",
  ],
  fontSizes: ["8pt", "10pt", "12pt", "14pt", "16pt", "18pt", "20pt", "24pt"],
  spacerWidths: [
    {
      name: "expansive",
      width: 72,
    },
    {
      name: "spacious",
      width: 56,
    },
    {
      name: "comfortable",
      width: 36,
    },
    {
      name: "cozy",
      width: 28,
    },
    {
      name: "compact",
      width: 18,
    },
  ],
  cellPadding: 3,
  borders: [
    {
      name: "level1",
      width: 3,
      color: "background2",
      type: "solid",
    },
    {
      name: "level2",
      width: 2,
      color: "accent1",
      type: "solid",
    },
    {
      name: "level3",
      width: 1,
      color: "text",
      type: "solid",
    },
    {
      name: "level4",
      width: 1,
      color: "shading2",
      type: "solid",
    },
    {
      name: "level5",
      width: 1,
      color: "shading2",
      type: "dashed",
    },
  ],
  lineSpacing: [
    {
      id: "8pt",
      paragraph: 1,
      line: 1,
    },
    {
      id: "9pt",
      paragraph: 1,
      line: 1,
    },
    {
      id: "10pt",
      paragraph: 2,
      line: 1.15,
    },
    {
      id: "11pt",
      paragraph: 2,
      line: 1.15,
    },
    {
      id: "12pt",
      paragraph: 2,
      line: 1.15,
    },
    {
      id: "13pt",
      paragraph: 3,
      line: 1.3,
    },
    {
      id: "14pt",
      paragraph: 3,
      line: 1.3,
    },
    {
      id: "15pt",
      paragraph: 3,
      line: 1.3,
    },
    {
      id: "16pt",
      paragraph: 4,
      line: 1.4,
    },
    {
      id: "17pt",
      paragraph: 4,
      line: 1.4,
    },
    {
      id: "18pt",
      paragraph: 4,
      line: 1.4,
    },
  ],
};
